import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HomeIcon, WrenchIcon, MapPinIcon, ShieldCheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function Hero() {
  const {
    t
  } = useLanguage();
  return <section className="relative min-h-screen w-full bg-black overflow-hidden" data-id="element-132">
      {/* Video Background - Professional construction site video */}
      <div className="absolute inset-0 z-0" data-id="element-133">
        <video autoPlay loop muted playsInline className="w-full h-full object-cover" style={{
        filter: 'brightness(0.45) saturate(1.1)'
      }} data-id="element-134">
          {/* Primary video - Real construction site (15 seconds, seamless loop) */}
          <source src="https://videos.pexels.com/video-files/3209828/3209828-uhd_2560_1440_25fps.mp4" type="video/mp4" data-id="element-135" />
          {/* Fallback video - Building construction process */}
          <source src="https://videos.pexels.com/video-files/3130284/3130284-uhd_2560_1440_30fps.mp4" type="video/mp4" data-id="element-136" />
        </video>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/60" data-id="element-137" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20" data-id="element-138" />
      </div>

      {/* Subtle logo watermark */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none" data-id="element-139">
        <span className="text-[20vw] font-black text-white/[0.03] tracking-tighter select-none" data-id="element-140">
          H
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 py-20 sm:py-24" data-id="element-141">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-5xl mx-auto text-center" data-id="element-142">
          {/* Badge */}
          <motion.div variants={itemVariants} className="mb-5 sm:mb-6" data-id="element-143">
            <motion.span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-xs sm:text-sm font-medium border border-white/20" whileHover={{
            scale: 1.02,
            backgroundColor: 'rgba(255,255,255,0.15)'
          }} data-id="element-144">
              <MapPinIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-145" />
              {t('hero.badge')}
            </motion.span>
          </motion.div>

          {/* Main heading - Light weight typography */}
          <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight mb-4 sm:mb-6 drop-shadow-2xl" data-id="element-146">
            <span className="text-white font-normal" data-id="element-147">{t('hero.title1')}</span>
            <br data-id="element-148" />
            <motion.span className="font-light bg-gradient-to-r from-white via-neutral-200 to-neutral-400 bg-clip-text text-transparent animate-gradient" style={{
            backgroundSize: '200% 200%'
          }} data-id="element-149">
              {t('hero.title2')}
            </motion.span>
          </motion.h1>

          {/* Subheading */}
          <motion.p variants={itemVariants} className="text-sm sm:text-base md:text-lg text-neutral-300 max-w-2xl mx-auto mb-6 sm:mb-8 leading-relaxed font-light px-2" data-id="element-150">
            {t('hero.subtitle')}
          </motion.p>

          {/* Trust badge */}
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mb-6 sm:mb-8" data-id="element-151">
            <ShieldCheckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white/70" data-id="element-152" />
            <span className="text-white/70 text-xs sm:text-sm font-medium" data-id="element-153">
              {t('hero.trust')}
            </span>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-3 justify-center px-4" data-id="element-154">
            <motion.div whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-155">
              <Link to="/construccion" className="group inline-flex items-center justify-center gap-2 px-5 sm:px-7 py-2.5 sm:py-3 bg-white text-black font-medium rounded-full transition-all duration-300 hover:bg-neutral-100 hover:shadow-[0_0_30px_rgba(255,255,255,0.25)] text-sm" data-id="element-156">
                <HomeIcon className="w-4 h-4" data-id="element-157" />
                {t('hero.cta.construction')}
                <span className="transition-transform group-hover:translate-x-0.5" data-id="element-158">
                  →
                </span>
              </Link>
            </motion.div>

            <motion.div whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-159">
              <Link to="/reformas" className="group inline-flex items-center justify-center gap-2 px-5 sm:px-7 py-2.5 sm:py-3 bg-white/10 backdrop-blur-sm text-white font-medium rounded-full border border-white/25 transition-all duration-300 hover:bg-white/15 hover:border-white/40 text-sm" data-id="element-160">
                <WrenchIcon className="w-4 h-4" data-id="element-161" />
                {t('hero.cta.renovations')}
                <span className="transition-transform group-hover:translate-x-0.5" data-id="element-162">
                  →
                </span>
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5,
        duration: 1
      }} className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2" data-id="element-163">
          <motion.div animate={{
          y: [0, 8, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-5 h-8 rounded-full border border-white/30 flex items-start justify-center p-1.5" data-id="element-164">
            <motion.div className="w-1 h-1 bg-white/70 rounded-full" animate={{
            opacity: [1, 0.4, 1]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} data-id="element-165" />
          </motion.div>
        </motion.div>
      </div>
    </section>;
}